import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Settings, Users, Bot, Save, Loader2, Plug, ExternalLink, CheckCircle2, AlertCircle, XCircle, Key, RefreshCw, ShieldCheck, Info, Clock, ChevronDown, ChevronUp, Database, Server, Globe, Tags, TestTube2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AGENTS = ['scout_agent', 'copywriter_agent', 'planner_agent', 'crm_agent', 'offering_agent', 'events_agent'];

const STATUS_MAP = {
  connected: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-emerald-200', label: 'Collegata' },
  partial: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200', label: 'Parziale' },
  error: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', label: 'Errore' },
  not_configured: { icon: XCircle, color: 'text-slate-400', bg: 'bg-slate-100', border: 'border-slate-200', label: 'Non configurata' },
};

function IntegrationRow({ integ, onVerify, onToggle, onSaveKeys, verifyingId, savingId, onRefresh }) {
  const sc = STATUS_MAP[integ.connection_status] || STATUS_MAP.not_configured;
  const StatusIcon = sc.icon;
  const isVerifying = verifyingId === integ.id;
  const isSaving = savingId === integ.id;
  const hasAuthUrl = integ.auth_url && integ.connection_status !== 'connected';
  const [expanded, setExpanded] = useState(false);
  const [keyValues, setKeyValues] = useState({});
  const [extraValues, setExtraValues] = useState({});

  // Init extra config values from server
  useEffect(() => {
    if (integ.extra_config) {
      setExtraValues(integ.extra_config);
    }
  }, [integ.extra_config]);

  const handleKeyChange = (keyName, value) => {
    setKeyValues(prev => ({ ...prev, [keyName]: value }));
  };

  const handleExtraChange = (key, value) => {
    setExtraValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // Filter out empty values
    const keysToSave = {};
    for (const [k, v] of Object.entries(keyValues)) {
      if (v && v.trim()) keysToSave[k] = v.trim();
    }
    const extraToSave = {};
    for (const [k, v] of Object.entries(extraValues)) {
      if (v !== undefined) extraToSave[k] = v;
    }
    await onSaveKeys(integ.id, keysToSave, extraToSave);
    setKeyValues({});
  };

  const hasUnsavedKeys = Object.values(keyValues).some(v => v && v.trim());
  const hasExtraFields = (integ.extra_fields || []).length > 0;
  const hasAnyEditableContent = Object.keys(integ.env_keys).length > 0 || hasExtraFields;

  return (
    <div className={`border ${sc.border} rounded-xl transition-all hover:shadow-sm`} data-testid={`integration-row-${integ.id}`}>
      {/* Header row */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${sc.bg}`}>
              <Plug className={`w-5 h-5 ${sc.color}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-[#1A3A5C]">{integ.name}</p>
                <Badge variant="outline" className="text-[10px] shrink-0">{integ.category_label}</Badge>
              </div>
              <p className="text-xs text-[#94A3B8] mt-0.5 line-clamp-1">{integ.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1.5">
              <StatusIcon className={`w-4 h-4 ${sc.color}`} />
              <span className={`text-xs font-medium ${sc.color}`}>{sc.label}</span>
            </div>
            <Switch
              checked={integ.is_active}
              onCheckedChange={(val) => onToggle(integ.id, val)}
              data-testid={`integration-toggle-${integ.id}`}
            />
          </div>
        </div>

        {/* Quick info + actions row */}
        <div className="mt-3 pl-[52px] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Key summary pills */}
            {Object.entries(integ.env_keys).map(([key, info]) => (
              <div key={key} className="flex items-center gap-1">
                <Key className={`w-3 h-3 ${info.configured ? 'text-emerald-500' : 'text-slate-300'}`} />
                <span className="text-[10px] text-[#64748B] font-mono">{key}</span>
                {info.configured ? (
                  <span className="text-[10px] text-emerald-600 font-mono">{info.masked_value}</span>
                ) : (
                  <span className="text-[10px] text-red-400 font-medium">mancante</span>
                )}
                {info.source === 'db' && <Database className="w-2.5 h-2.5 text-blue-400" title="Salvata nel pannello" />}
                {info.source === 'env' && <Server className="w-2.5 h-2.5 text-slate-400" title="Variabile d'ambiente" />}
              </div>
            ))}

            {/* Last verification */}
            {integ.last_verify_status && (
              <div className="flex items-center gap-1 text-[10px]">
                <Clock className="w-3 h-3 text-[#94A3B8]" />
                <span className={integ.last_verify_status === 'connected' ? 'text-emerald-600' : 'text-red-500'}>
                  {integ.last_verify_detail}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" onClick={() => onVerify(integ.id)} disabled={isVerifying} data-testid={`verify-btn-${integ.id}`}>
              {isVerifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              Verifica
            </Button>
            {hasAuthUrl && (
              <a href={integ.auth_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 h-7 px-3 text-xs font-medium text-[#2E75B6] border border-[#2E75B6]/30 rounded-md hover:bg-[#2E75B6]/5 transition-colors"
                data-testid={`auth-link-${integ.id}`}>
                <ExternalLink className="w-3 h-3" /> Autorizza
              </a>
            )}
            {integ.docs_url && (
              <a href={integ.docs_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-7 px-2 text-[10px] text-[#94A3B8] hover:text-[#64748B]">
                <Info className="w-3 h-3" /> Docs
              </a>
            )}
            {hasAnyEditableContent && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setExpanded(!expanded)} data-testid={`expand-btn-${integ.id}`}>
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>

        {/* Error detail */}
        {integ.connection_status === 'error' && integ.last_verify_detail && (
          <div className="mt-2 ml-[52px] flex items-start gap-1.5 bg-red-50 rounded-md px-2.5 py-1.5">
            <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-red-600">{integ.last_verify_detail}</p>
          </div>
        )}
      </div>

      {/* Expanded: key editing + extra config */}
      {expanded && (
        <div className="border-t border-[#E2E8F0] bg-[#FAFBFC] px-4 py-4 rounded-b-xl" data-testid={`config-panel-${integ.id}`}>
          <div className="pl-[52px] space-y-4">
            {/* API Key inputs */}
            <div>
              <p className="text-xs font-semibold text-[#1A3A5C] mb-2">Chiavi API</p>
              <div className="grid gap-3">
                {Object.entries(integ.env_keys).map(([keyName, info]) => (
                  <div key={keyName} className="flex items-center gap-3">
                    <Label className="text-[11px] font-mono text-[#64748B] w-56 shrink-0">{keyName}</Label>
                    <div className="flex-1 relative">
                      <Input
                        type="password"
                        placeholder={info.configured ? info.masked_value : `Inserisci ${keyName}`}
                        value={keyValues[keyName] || ''}
                        onChange={(e) => handleKeyChange(keyName, e.target.value)}
                        className="h-8 text-xs font-mono pr-16"
                        data-testid={`key-input-${integ.id}-${keyName}`}
                      />
                      {info.configured && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-emerald-500 font-medium">
                          {info.source === 'db' ? 'DB' : 'ENV'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Extra config fields */}
            {hasExtraFields && (
              <div>
                <p className="text-xs font-semibold text-[#1A3A5C] mb-2">Configurazione aggiuntiva</p>
                <div className="grid gap-3">
                  {integ.extra_fields.map((field) => (
                    <div key={field.key} className="flex items-center gap-3">
                      <Label className="text-[11px] text-[#64748B] w-56 shrink-0">
                        {field.label}
                        {field.required && <span className="text-red-400 ml-0.5">*</span>}
                      </Label>
                      <Input
                        placeholder={field.placeholder || `Inserisci ${field.label}`}
                        value={extraValues[field.key] || ''}
                        onChange={(e) => handleExtraChange(field.key, e.target.value)}
                        className="h-8 text-xs font-mono flex-1"
                        data-testid={`extra-input-${integ.id}-${field.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Endpoints */}
            {integ.endpoints.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#1A3A5C] mb-1">Endpoint attivi</p>
                <div className="flex flex-wrap gap-1.5">
                  {integ.endpoints.map((ep, i) => (
                    <span key={i} className="text-[9px] text-[#64748B] font-mono bg-white border border-[#E2E8F0] rounded px-2 py-0.5">{ep}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Auth instructions */}
            {integ.auth_instructions && (
              <p className="text-[10px] text-[#94A3B8] italic leading-relaxed">{integ.auth_instructions}</p>
            )}

            {/* Save button */}
            <div className="flex items-center gap-3 pt-1">
              <Button
                size="sm"
                className="h-8 bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white gap-1.5"
                onClick={handleSave}
                disabled={isSaving || (!hasUnsavedKeys && Object.keys(extraValues).length === 0)}
                data-testid={`save-keys-btn-${integ.id}`}
              >
                {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Salva configurazione
              </Button>
              {hasUnsavedKeys && (
                <span className="text-[10px] text-amber-600">Modifiche non salvate</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const [agentConfigs, setAgentConfigs] = useState({});
  const [orgUsers, setOrgUsers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [integrationSummary, setIntegrationSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [verifyingAll, setVerifyingAll] = useState(false);

  const loadIntegrations = useCallback(async () => {
    try {
      const intRes = await api.get('/integrations');
      setIntegrations(intRes.data.integrations || []);
      setIntegrationSummary(intRes.data.summary || {});
    } catch {}
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const configs = {};
        for (const agent of AGENTS) {
          try {
            const res = await api.get(`/agents/${agent}/config`);
            configs[agent] = res.data;
          } catch {}
        }
        setAgentConfigs(configs);
        try {
          const usersRes = await api.get('/users');
          setOrgUsers(usersRes.data);
        } catch {}
        await loadIntegrations();
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [loadIntegrations]);

  const verifySingle = async (integId) => {
    setVerifyingId(integId);
    try {
      const res = await api.post(`/integrations/${integId}/verify`);
      const { status, detail } = res.data;
      if (status === 'connected') toast.success(`${integId}: ${detail}`);
      else if (status === 'not_configured') toast.warning(`${integId}: ${detail}`);
      else toast.error(`${integId}: ${detail}`);
      await loadIntegrations();
    } catch { toast.error('Errore durante la verifica'); }
    finally { setVerifyingId(null); }
  };

  const verifyAll = async () => {
    setVerifyingAll(true);
    try {
      const res = await api.post('/integrations/verify-all');
      const results = res.data.results || [];
      const ok = results.filter(r => r.status === 'connected').length;
      const fail = results.filter(r => r.status === 'error').length;
      const skip = results.filter(r => r.status === 'not_configured').length;
      toast.success(`Verifica completata: ${ok} connesse, ${fail} errori, ${skip} non configurate`);
      await loadIntegrations();
    } catch { toast.error('Errore durante la verifica globale'); }
    finally { setVerifyingAll(false); }
  };

  const handleToggle = async (integId, val) => {
    try {
      await api.put(`/integrations/${integId}`, { is_active: val });
      setIntegrations(prev => prev.map(i => i.id === integId ? { ...i, is_active: val } : i));
      const name = integrations.find(i => i.id === integId)?.name || integId;
      toast.success(`${name} ${val ? 'attivata' : 'disattivata'}`);
    } catch { toast.error('Errore aggiornamento'); }
  };

  const handleSaveKeys = async (integId, keys, extraConfig) => {
    setSavingId(integId);
    try {
      const res = await api.post(`/integrations/${integId}/keys`, { keys, extra_config: extraConfig });
      const savedKeys = res.data.saved_keys || [];
      const savedExtra = res.data.saved_extra || [];
      const parts = [];
      if (savedKeys.length) parts.push(`${savedKeys.length} chiave/i`);
      if (savedExtra.length) parts.push(`${savedExtra.length} configurazione/i`);
      toast.success(`${integId}: ${parts.join(' e ')} salvate`);
      await loadIntegrations();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSavingId(null); }
  };

  const saveAgentConfig = async (agentName) => {
    try {
      const cfg = agentConfigs[agentName];
      await api.patch(`/agents/${agentName}/config`, { config: cfg.config, is_active: cfg.is_active });
      toast.success(`Configurazione ${agentName.replace(/_/g, ' ')} salvata`);
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const toggleAgent = (agentName, active) => {
    setAgentConfigs(prev => ({ ...prev, [agentName]: { ...prev[agentName], is_active: active } }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1A3A5C]" /></div>;

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">Impostazioni</h1>
        <p className="page-subtitle">Configurazione sistema e agenti</p>
      </div>

      <Tabs defaultValue="integrations">
        <TabsList className="bg-white border border-[#E2E8F0] mb-6">
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations"><Plug className="w-4 h-4 mr-2" />Integrazioni</TabsTrigger>
          <TabsTrigger value="agents" data-testid="settings-tab-agents"><Bot className="w-4 h-4 mr-2" />Agenti</TabsTrigger>
          <TabsTrigger value="users" data-testid="settings-tab-users"><Users className="w-4 h-4 mr-2" />Utenti</TabsTrigger>
          <TabsTrigger value="org" data-testid="settings-tab-org"><Settings className="w-4 h-4 mr-2" />Organizzazione</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations">
          {/* Summary + Verify All */}
          <div className="flex items-end justify-between mb-6 gap-4">
            <div className="grid grid-cols-4 gap-3 flex-1">
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-3 flex items-center gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-xl font-bold text-emerald-700">{integrationSummary.connected || 0}</p>
                    <p className="text-[10px] text-emerald-600">Collegate</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50/50">
                <CardContent className="p-3 flex items-center gap-2.5">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="text-xl font-bold text-red-600">{integrationSummary.error || 0}</p>
                    <p className="text-[10px] text-red-500">Errori</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-3 flex items-center gap-2.5">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-xl font-bold text-amber-700">{integrationSummary.partial || 0}</p>
                    <p className="text-[10px] text-amber-600">Parziali</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-slate-50/50">
                <CardContent className="p-3 flex items-center gap-2.5">
                  <XCircle className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xl font-bold text-slate-500">{integrationSummary.not_configured || 0}</p>
                    <p className="text-[10px] text-slate-500">Da configurare</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            <Button onClick={verifyAll} disabled={verifyingAll} className="h-10 bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white gap-2 shrink-0" data-testid="verify-all-btn">
              {verifyingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Verifica Tutto
            </Button>
          </div>

          {/* Integration cards */}
          <div className="space-y-3" data-testid="integrations-list">
            {integrations.map((integ) => (
              <IntegrationRow
                key={integ.id}
                integ={integ}
                onVerify={verifySingle}
                onToggle={handleToggle}
                onSaveKeys={handleSaveKeys}
                verifyingId={verifyingId}
                savingId={savingId}
                onRefresh={loadIntegrations}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="agents">
          <div className="space-y-4">
            {AGENTS.map(agent => {
              const cfg = agentConfigs[agent];
              if (!cfg) return null;
              return (
                <Card key={agent} className="border-[#E2E8F0]" data-testid={`agent-config-${agent}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Bot className="w-5 h-5 text-[#2E75B6]" />
                        <div>
                          <h3 className="text-sm font-semibold text-[#1A3A5C] capitalize">{agent.replace(/_/g, ' ')}</h3>
                          <p className="text-xs text-[#64748B]">v{cfg.version} — Ultimo aggiornamento: {cfg.last_modified_by}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch checked={cfg.is_active} onCheckedChange={(v) => toggleAgent(agent, v)} data-testid={`agent-toggle-${agent}`} />
                        <Badge className={cfg.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>{cfg.is_active ? 'Attivo' : 'Disattivo'}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {Object.entries(cfg.config || {}).map(([key, val]) => (
                        <div key={key} className="space-y-1">
                          <Label className="text-xs text-[#64748B]">{key.replace(/_/g, ' ')}</Label>
                          <Input value={typeof val === 'object' ? JSON.stringify(val) : String(val)} onChange={(e) => {
                            const newCfg = { ...cfg, config: { ...cfg.config, [key]: isNaN(e.target.value) ? e.target.value : Number(e.target.value) } };
                            setAgentConfigs(prev => ({ ...prev, [agent]: newCfg }));
                          }} className="h-8 text-xs" />
                        </div>
                      ))}
                    </div>
                    <Button size="sm" onClick={() => saveAgentConfig(agent)} className="mt-3 bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" data-testid={`save-agent-${agent}`}>
                      <Save className="w-3 h-3 mr-1" /> Salva
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Card className="border-[#E2E8F0]">
            <CardHeader><CardTitle className="text-lg text-[#1A3A5C]">Utenti dell'organizzazione</CardTitle></CardHeader>
            <CardContent>
              {orgUsers.length === 0 ? <p className="text-sm text-[#64748B]">Nessun utente trovato</p> : (
                <div className="space-y-3">
                  {orgUsers.map(u => (
                    <div key={u.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg" data-testid={`user-${u.id}`}>
                      <div>
                        <p className="text-sm font-medium text-[#1A3A5C]">{u.full_name}</p>
                        <p className="text-xs text-[#64748B]">{u.email}</p>
                      </div>
                      <Badge variant="secondary">{u.role}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="org">
          <Card className="border-[#E2E8F0]">
            <CardHeader><CardTitle className="text-lg text-[#1A3A5C]">Dettagli organizzazione</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><Label className="text-xs text-[#64748B]">Nome</Label><p className="font-medium text-[#1A3A5C]">{organization?.name}</p></div>
                <div><Label className="text-xs text-[#64748B]">Slug</Label><p className="font-medium text-[#1A3A5C]">{organization?.slug}</p></div>
                <div><Label className="text-xs text-[#64748B]">Piano</Label><Badge variant="secondary" className="mt-1">{organization?.plan}</Badge></div>
                <div><Label className="text-xs text-[#64748B]">Creazione</Label><p className="font-medium text-[#1A3A5C]">{organization?.created_at ? new Date(organization.created_at).toLocaleDateString('it-CH') : '-'}</p></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick access — pagine di configurazione */}
      <div className="mt-6" data-testid="settings-quick-access">
        <h3 className="text-sm font-medium text-[#64748B] mb-3">Accesso rapido</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: 'Profili Social', to: '/social-profiles', icon: Globe, desc: 'Configura i profili social collegati' },
            { label: 'Gestione Tag', to: '/tags', icon: Tags, desc: 'Gestisci i tag per organizzare i contenuti' },
            { label: 'Tester UX', to: '/tester', icon: TestTube2, desc: 'Strumenti di debug e test agenti' },
          ].map(link => {
            const LinkIcon = link.icon;
            return (
              <Card key={link.to}
                className="border-[#E2E8F0] cursor-pointer hover:border-[#2E75B6]/40 hover:shadow-sm transition-all"
                onClick={() => navigate(link.to)}
                data-testid={`quick-access-${link.to.slice(1)}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
                    <LinkIcon className="w-4 h-4 text-[#64748B]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A3A5C]">{link.label}</p>
                    <p className="text-[10px] text-[#94A3B8]">{link.desc}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
